import React, { useState, useEffect, useRef } from 'react';
import { Loader2, X, ExternalLink, ShoppingBag, Calendar, User } from 'lucide-react';
import type { CustomerDetail } from '../../../api/customers';
import { fetchCustomerDetail } from '../../../api/customers';

export function formatCurrency(n: number): string {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatDate(d: string | null | undefined): string {
  if (!d) return '--';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function statusBadge(status: string) {
  const colors: Record<string, string> = {
    confirmed: 'bg-moss-100 text-moss-800',
    completed: 'bg-moss-100 text-moss-800',
    cancelled: 'bg-red-100 text-red-800',
    rebooked: 'bg-water-100 text-water-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-stone-100 text-stone-700'}`}>
      {status}
    </span>
  );
}

function CustomerDetailModal({
  email,
  onClose,
}: {
  email: string;
  onClose: () => void;
}) {
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchCustomerDetail(email)
      .then(setCustomer)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [email]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 overflow-y-auto"
    >
      <div className="bg-white dark:bg-[#1a1f1c] rounded-xl shadow-2xl w-full max-w-4xl mb-12 max-h-[calc(100vh-6rem)] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#1a1f1c] border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div>
            {loading ? (
              <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
                <Loader2 size={16} className="animate-spin" /> Loading...
              </div>
            ) : error ? (
              <span className="text-red-600">{error}</span>
            ) : (
              <>
                <h2 className="text-lg font-bold text-bark-900 dark:text-[#e8ece9]">
                  {customer!.firstName} {customer!.lastName}
                </h2>
                <div className="flex items-center gap-3 text-sm text-stone-500 dark:text-stone-400 mt-0.5">
                  <span>{customer!.email}</span>
                  {customer!.phone && <span>{customer!.phone}</span>}
                </div>
              </>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 dark:hover:bg-[#242d28] rounded-lg">
            <X size={18} />
          </button>
        </div>

        {customer && !loading && !error && (
          <div className="px-6 py-5 space-y-6">
            {/* Stats grid */}
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: 'Total Spent', value: formatCurrency(customer.stats.totalSpent), color: 'text-moss-600' },
                { label: 'Refunded', value: formatCurrency(customer.stats.totalRefunded), color: 'text-red-500' },
                { label: 'Workshops', value: customer.stats.workshopCount.toString(), color: 'text-water-600' },
                { label: 'Avg Group', value: customer.stats.avgGroupSize.toFixed(1), color: 'text-purple-600' },
                { label: 'Avg Ticket', value: formatCurrency(customer.stats.avgTicketPrice), color: 'text-clay-600' },
              ].map((s) => (
                <div key={s.label} className="bg-stone-50 dark:bg-[#1e2520] rounded-lg p-3 text-center">
                  <p className="text-xs text-stone-500 dark:text-stone-400">{s.label}</p>
                  <p className={`text-lg font-bold ${s.color} mt-0.5`}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Customer Info */}
            {(customer.customerSince || customer.referralSource || customer.dateOfBirth || customer.notes) && (
              <div className="bg-stone-50 dark:bg-[#1e2520] rounded-lg p-4">
                <h3 className="text-sm font-semibold text-bark-900 dark:text-[#e8ece9] mb-2 flex items-center gap-1.5">
                  <User size={14} className="text-water-600" />
                  Customer Info
                </h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {customer.customerSince && (
                    <div>
                      <span className="text-stone-500 dark:text-stone-400">Customer since: </span>
                      <span className="text-bark-900 dark:text-[#e8ece9]">{formatDate(customer.customerSince)}</span>
                    </div>
                  )}
                  {customer.referralSource && (
                    <div>
                      <span className="text-stone-500 dark:text-stone-400">Referral source: </span>
                      <span className="text-bark-900 dark:text-[#e8ece9]">{customer.referralSource}</span>
                    </div>
                  )}
                  {customer.dateOfBirth && (
                    <div>
                      <span className="text-stone-500 dark:text-stone-400">Date of birth: </span>
                      <span className="text-bark-900 dark:text-[#e8ece9]">{formatDate(customer.dateOfBirth)}</span>
                    </div>
                  )}
                  {customer.notes && (
                    <div className="col-span-2">
                      <span className="text-stone-500 dark:text-stone-400">Notes: </span>
                      <span className="text-bark-900 dark:text-[#e8ece9]">{customer.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Workshop History */}
            {(() => {
              const workshopMap = new Map<string, {
                workshopId: string;
                workshopTitle: string;
                workshopDate: string;
                venue: string;
                venueCity: string;
                species: string;
                region: string;
                ticketCount: number;
                totalPaid: number;
                totalGroupSize: number;
                status: string;
              }>();
              for (const t of customer.tickets) {
                const existing = workshopMap.get(t.workshopId);
                if (existing) {
                  existing.ticketCount += 1;
                  existing.totalPaid += t.pricePaid;
                  existing.totalGroupSize += t.customerCount;
                  // Derive combined status: completed > confirmed > cancelled
                  const priority: Record<string, number> = { completed: 3, confirmed: 2, rebooked: 1, cancelled: 0 };
                  if ((priority[t.status] ?? 0) > (priority[existing.status] ?? 0)) {
                    existing.status = t.status;
                  }
                } else {
                  workshopMap.set(t.workshopId, {
                    workshopId: t.workshopId,
                    workshopTitle: t.workshopTitle,
                    workshopDate: t.workshopDate,
                    venue: t.venue,
                    venueCity: t.venueCity,
                    species: t.species,
                    region: t.region,
                    ticketCount: 1,
                    totalPaid: t.pricePaid,
                    totalGroupSize: t.customerCount,
                    status: t.status,
                  });
                }
              }
              const workshops = Array.from(workshopMap.values()).sort(
                (a, b) => new Date(b.workshopDate).getTime() - new Date(a.workshopDate).getTime()
              );
              if (workshops.length === 0) return null;

              const regionColors: Record<string, string> = {
                'Pacific Northwest': 'bg-water-100 text-water-800 dark:bg-water-900/30 dark:text-water-300',
                'California': 'bg-clay-100 text-clay-800 dark:bg-clay-900/30 dark:text-clay-300',
              };

              return (
                <div>
                  <h3 className="text-sm font-semibold text-bark-900 dark:text-[#e8ece9] mb-2 flex items-center gap-1.5">
                    <Calendar size={14} className="text-water-600" />
                    Workshop History ({workshops.length})
                  </h3>
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {workshops.map((w) => (
                      <div
                        key={w.workshopId}
                        className="bg-stone-50 dark:bg-[#1e2520] rounded-lg p-3 border min-w-[220px] max-w-[260px] flex-shrink-0 flex flex-col gap-1.5"
                      >
                        <p className="font-semibold text-sm text-bark-900 dark:text-[#e8ece9] truncate" title={w.species}>
                          {w.species}
                        </p>
                        <p className="text-xs text-stone-600 dark:text-stone-400 truncate" title={w.workshopTitle}>
                          {w.workshopTitle}
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400">
                          {formatDate(w.workshopDate)}
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
                          {w.venue}{w.venueCity ? `, ${w.venueCity}` : ''}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {w.region && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${regionColors[w.region] || 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300'}`}>
                              {w.region}
                            </span>
                          )}
                          {statusBadge(w.status)}
                        </div>
                        <p className="text-xs text-stone-400 dark:text-stone-500 mt-auto pt-1 border-t border-stone-200 dark:border-stone-700">
                          {w.ticketCount} ticket{w.ticketCount !== 1 ? 's' : ''} · {formatCurrency(w.totalPaid)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Tickets table */}
            <div>
              <h3 className="text-sm font-semibold text-bark-900 dark:text-[#e8ece9] mb-2">
                Tickets ({customer.tickets.length})
                {customer.stats.cancelledCount > 0 && (
                  <span className="text-stone-400 font-normal ml-1">
                    incl. {customer.stats.cancelledCount} cancelled
                  </span>
                )}
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 dark:bg-[#1e2520] text-stone-600 dark:text-stone-400 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Workshop</th>
                      <th className="px-3 py-2 font-medium">Venue</th>
                      <th className="px-3 py-2 font-medium">Species</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium text-right">Paid</th>
                      <th className="px-3 py-2 font-medium text-right">Group</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {customer.tickets.map((t) => (
                      <tr key={t.id} className="hover:bg-stone-50 dark:hover:bg-[#242d28]">
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(t.workshopDate)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[200px]">{t.workshopTitle}</span>
                            {t.fareharborDashboardUrl && (
                              <a
                                href={t.fareharborDashboardUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-stone-400 hover:text-moss-600 flex-shrink-0"
                              >
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-stone-600 dark:text-stone-400">{t.venue}</td>
                        <td className="px-3 py-2 text-stone-600 dark:text-stone-400">{t.species}</td>
                        <td className="px-3 py-2">{statusBadge(t.status)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(t.pricePaid)}</td>
                        <td className="px-3 py-2 text-right">{t.customerCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Shopify orders */}
            {customer.shopifyOrders.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-bark-900 dark:text-[#e8ece9] mb-2">
                  <ShoppingBag size={14} className="inline mr-1 -mt-0.5" />
                  Shopify Orders ({customer.shopifyOrders.length})
                  <span className="text-stone-400 font-normal ml-2">
                    {formatCurrency(customer.stats.shopifyTotalSpent)} total
                  </span>
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 dark:bg-[#1e2520] text-stone-600 dark:text-stone-400 text-left">
                      <tr>
                        <th className="px-3 py-2 font-medium">Order #</th>
                        <th className="px-3 py-2 font-medium">Date</th>
                        <th className="px-3 py-2 font-medium">Items</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {customer.shopifyOrders.map((o) => (
                        <tr key={o.id} className="hover:bg-stone-50 dark:hover:bg-[#242d28]">
                          <td className="px-3 py-2 font-mono text-xs">#{o.orderNumber}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(o.orderDate)}</td>
                          <td className="px-3 py-2 text-stone-600 dark:text-stone-400">
                            {o.lineItems.map((li) => `${li.title} (x${li.quantity})`).join(', ')}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              o.cancelledAt
                                ? 'bg-red-100 text-red-800'
                                : o.financialStatus === 'paid'
                                ? 'bg-moss-100 text-moss-800'
                                : 'bg-stone-100 text-stone-700'
                            }`}>
                              {o.cancelledAt ? 'cancelled' : o.financialStatus}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{formatCurrency(o.totalPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Combined total */}
            {customer.shopifyOrders.length > 0 && (
              <div className="bg-moss-50 dark:bg-moss-900/20 border border-moss-200 dark:border-moss-800 rounded-lg px-4 py-3 flex items-center justify-between text-sm">
                <span className="text-moss-800 dark:text-moss-300 font-medium">Combined Lifetime Value (Workshops + Shopify)</span>
                <span className="text-moss-900 dark:text-moss-200 font-bold text-lg">{formatCurrency(customer.stats.combinedTotalSpent)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDetailModal;
